<script setup lang="ts">
// TODO :: TASK-2103 - Should be promoted to a template
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { ProductAtCart } from '@ProductsAndServicesModule';
import { useAToast } from '@Atoms';
import { useCartSteps } from '../../../../composables';
import { CART_STEPS } from '../../../../enums';
import { AddProductToOutcomeCartService } from '../../../../services';
import useShoppingCartStore from '../../../../store';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const productListRef = ref<{ clearQuantityForProduct: (productId: string) => void } | null>(null);

const { cartData, loading, mainLoading } = storeToRefs(useShoppingCartStore());
const { resetCart } = useShoppingCartStore();

const { notify, TOAST_TYPES } = useAToast();
const { currentStep, previousStep, nextStep, nextStepButton } = useCartSteps();

const TOAST_ERROR = {
  message: 'cart.toast.addItem.error.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const TOAST_SUCCESS = {
  title: 'cart.toast.addItem.success.title',
  type: TOAST_TYPES.SUCCESS,
};

onMounted(() => (currentStep.value = CART_STEPS.PRODUCTS_AND_SERVICES));

// EVENTS
function onNextStep() {
  emits('onStepUpdate', nextStep.value.step!.name);
}

async function onAddToCart(product: ProductAtCart) {
  loading.value = true;

  const items = product.type === 'product' ? cartData.value?.products : cartData.value?.services;
  const currentQty = items?.find((item: ProductAtCart) => item._id === product._id)?.quantity ?? 0;
  const delta = (product.quantity ?? 0) - currentQty;

  const PAYLOAD = {
    products: [{ ...product, quantity: delta }],
  };

  await AddProductToOutcomeCartService(PAYLOAD)
    .then((data) => onSuccess(product, data))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

async function onMinusFromCart(product: ProductAtCart, quantityToRemove: number) {
  loading.value = true;

  const PAYLOAD = {
    products: [{ ...product, quantity: -quantityToRemove }],
  };

  await AddProductToOutcomeCartService(PAYLOAD)
    .then((data) => onSuccess(product, data))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function isCartEmptyAfterRemoval(data: any): boolean {
  const cartType = data?.cart_type;
  const isProductsOnlyCart = cartType === 'product' || cartType === 'service';
  const hasNoProducts = !data?.products?.length;
  const hasNoServices = !data?.services?.length;

  return isProductsOnlyCart && hasNoProducts && hasNoServices;
}

function onSuccess(product: ProductAtCart, data: any) {
  if (isCartEmptyAfterRemoval(data)) {
    resetCart();
    mainLoading.value = false;
    return;
  }

  notify(TOAST_SUCCESS);
  cartData.value = data;
  productListRef.value?.clearQuantityForProduct(product._id);
}
</script>

<template>
  <main>
    <OProductAndServicesList
      ref="productListRef"
      :loading
      @onAddToCart="onAddToCart"
      @onMinusFromCart="onMinusFromCart"
    />
  </main>

  <footer class="flex-shrink">
    <div class="flex justify-between">
      <Button
        v-if="previousStep"
        outlined
        class="af-button af-button-success-outlined w-fit"
        icon="pi pi-arrow-left"
        :label="$t('common.back')"
        @click="emits('onStepUpdate', previousStep)"
      />

      <Button
        class="af-button af-button-success ml-auto"
        iconPos="right"
        :label="$t(nextStepButton.label)"
        @click="onNextStep"
      >
        <template #icon>
          <FaIcon
            v-if="nextStepButton.icon"
            class="mr-2"
            :icon="$icons[nextStepButton.icon]"
          />
        </template>
      </Button>
    </div>
  </footer>
</template>
